import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setMobileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-background-50 border-b border-background-200/40'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-[72px]">

            <Link
              to="/"
              onClick={scrollToTop}
              className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
            >
              OOPBuy Guide
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                onClick={scrollToTop}
                className="text-foreground-700 hover:text-foreground-950 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Home
              </Link>

              <Link
                to="/oopbuy-spreadsheet-guide"
                onClick={scrollToTop}
                className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
              </Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 text-base ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-2 max-h-80 overflow-y-auto animate-[fadeIn_200ms_ease] z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/oopbuy-spreadsheet-buying-guide"
                onClick={scrollToTop}
                className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Buying Tips
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-primary-500 text-primary-600 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                OOPBuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-4 md:px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          <div
            ref={mobileMenuRef}
            className="absolute top-0 right-0 w-[300px] max-w-[85vw] h-full bg-background-50 shadow-2xl overflow-y-auto animate-[slideInRight_250ms_ease]"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/70">
              <span className="font-heading text-lg font-bold text-foreground-950">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-5 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:text-foreground-950 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line text-lg w-6 text-center"></i>
                Home
              </Link>

              <Link
                to="/oopbuy-spreadsheet-guide"
                onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:text-foreground-950 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer"
              >
                <i className="ri-guide-line text-lg w-6 text-center"></i>
                Shopping Guide
              </Link>

              <Link
                to="/oopbuy-spreadsheet-buying-guide"
                onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:text-foreground-950 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer"
              >
                <i className="ri-lightbulb-line text-lg w-6 text-center"></i>
                Buying Tips
              </Link>

              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:text-foreground-950 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg w-6 text-center"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-300 text-base ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileDropdownOpen && (
                <div className="ml-9 space-y-0.5 border-l-2 border-background-200/60 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200/60 my-3 pt-3 space-y-2">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:text-primary-600 hover:bg-primary-50 text-sm font-semibold transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-external-link-line text-lg w-6 text-center"></i>
                  OOPBuy Spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-bag-line"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes slideInRight {
              0% { transform: translateX(100%); }
              100% { transform: translateX(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}